import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Router, { useRouter } from "next/router";
import classnames from "classnames/bind";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Button, Spinner } from "@aqar-app/aqar-design-system";
import { Button as UIButton } from "@aqar-app/react-ui";
import InfiniteScroll from "react-infinite-scroll-component";
import dayjs from "dayjs";
import { Icon } from "@/components/icon";
import { FETCH_SYSTEM_MSGS } from "../../../graphql/queries/user";
import useDelayedUnmount from "../../../hooks/useDelayedUnmount";
import { LOGOUT } from "../../../graphql/mutations/auth";
import { useAuthState } from "../../../contexts/auth/auth.provider";
import { useAppState } from "../../../contexts/app/app.provider";
import { setCookie, setLoginCookies } from "../../../lib/cookiesHelpers";
import { buildFileSrc, financialFormat } from "../../../lib/helpers";
import config from "../../../common/config";
import styles from "./sidebar.module.scss";
import {
  trackCloseUserMenu,
  trackOpenContactUs,
  trackOpenNotifs,
  trackOpenUserProfile,
} from "../../../lib/tracking/trackNavigationEvents";
import { usePath } from "@/src/contexts/path/path";
import { INTERCOM_EVENT } from "@/components/intercom";
import { trackLogoutEvent } from "@/lib/tracking/trackAuthenticationEvents";
import style from "@/src/pages/user/user.module.scss";

const cx = classnames.bind(styles);
export function linkify(text) {
  const urlRegex =
    /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\\/%?=~_|!:,.;]*[-A-Z0-9+&@#\\/%=~_|])/gi;
  return text?.replace(urlRegex, (url) => {
    return `<a href="${url}">${url}</a>`;
  });
}

const ACTIVITIES_LINKS = [
  {
    title: "إعلاناتي",
    subTitle: "الإعلانات المنشورة والمنتهية",
    link: `/my-listings`,
    filter: () => true,
  },
  {
    title: "التراخيص",
    subTitle: "متابعة تراخيص الإعلانات",
    link: `/mlr`,
    filter: () => true,
  },
  // {
  //   title: "صفقاتي",
  //   subTitle: "توثيق الصفقات التي قمت بإتمامها",
  //   link: (user) => `/user/${user?.user_id}`,
  //   filter: () => true,
  // },
  {
    title: "طلباتي",
    subTitle: "الحصول على تنبيهات بالعروض المطابقة لرغباتك",
    link: "/user/searches",
    filter: () => true,
  },
  {
    title: "الحجوزات",
    subTitle: "سجل حجوزات وحدات التأجير اليومي/الشهري",
    link: "/user/bookings",
    filter: () => true,
  },
  {
    title: "طلبات الدفع",
    subTitle: "سجل طلبات حجز وحدات المشاريع",
    link: "/user/payment-requests",
    filter: (user) =>
      user?.permissions?.includes(
        "ACCESS_TO_INIT_REAL_ESTATE_PROJECT_PAYMENT_REQUEST",
      ),
  },
  {
    title: "طلبات حجز الوحدات",
    subTitle: "سجل طلبات حجز وحدات المشاريع",
    link: "/user/my-payment-requests",
    filter: () => true,
  },
];

const OFFICE_LINKS = [
  {
    title: "لوحة التحكم",
    link: `${config.AQAR_WEB}/offices-management`,
    filter: () => true,
  },
  {
    title: "توثيق المكتب",
    link: `/user/verify-office`,
    filter: (user) => user?.type !== 1,
  },
];

const FINANCIAL_LINKS = [
  {
    title: (user) =>
      `المحفظة (${
        user?.points_balance
          ? financialFormat(user?.points_balance * 0.01) + ") ريال"
          : "لا يوجد رصيد)"
      }`,
    link: `/user/wallet`,
    filter: () => true,
  },
  {
    title: "المدفوعات",
    link: (user) => `/user/${user?.user_id}#fees`,
    filter: () => true,
  },
  {
    title: "الحساب البنكي ومعلومات الضريبة",
    link: "/user-actions/financial-info",
    filter: () => true,
  },
  {
    title: "إدارة البطاقات",
    link: "/user-actions/cards-settings",
    filter: () => true,
  },
];

const PROFILE_LINKS = [
  {
    title: "تعديل الملف الشخصي",
    link: "/user-actions/profile-update",
    filter: () => true,
  },
  {
    title: "تغيير كلمة المرور",
    link: "/user-actions/update-password",
    filter: () => true,
  },
  {
    title: "تغيير الجوال",
    link: "/user-actions/update-phone",
    filter: () => true,
  },
];

const UserSidebarV2 = () => {
  const apolloClient = useApolloClient();
  const { msgsCount, updateContext: updateAppContext } = useAppState();
  const [showUserSidebar, setShowUserSidebar] = useState(false);
  const { updateContext, token, user, ...restAuth } = useAuthState();
  const path = usePath();
  const shouldRender = useDelayedUnmount(showUserSidebar, 500);
  const UserSidebarRef = useRef(null);
  const [showUserNotification, toggleShowUserNotification] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [msgsOffset, setMsgsOffset] = useState(undefined);

  const router = useRouter();

  const { loading, fetchMore: fetchMoreMsgs } = useQuery(FETCH_SYSTEM_MSGS, {
    skip:
      !showUserNotification ||
      msgsOffset ||
      msgsOffset === null ||
      !user?.user_id,
    context: { token },
    variables: { msg_id: `${user?.user_id}-0` },
    onCompleted: (res) => {
      const newMsgs = res?.Chat?.threads?.list || [];
      const lastOffset = res?.Chat?.threads?.offset || [];
      setMsgs(newMsgs);
      setMsgsOffset(lastOffset);
    },
  });

  const [logoutMutation, { loading: logoutLoading }] = useMutation(LOGOUT, {
    context: { token },
    // eslint-disable-next-line no-use-before-define
  });

  const handleClickOutside = (event) => {
    if (
      UserSidebarRef.current &&
      !UserSidebarRef.current.contains(event.target) &&
      showUserSidebar
    ) {
      trackCloseUserMenu();
      setShowUserSidebar(!showUserSidebar);
    }
  };

  const closeUserSideBar = () => {
    trackCloseUserMenu();
    if (showUserSidebar && !logoutLoading) setShowUserSidebar(!showUserSidebar);
  };

  useEffect(() => {
    if (!showUserSidebar) return;
    document.addEventListener("click", handleClickOutside, true);
    Router.events.on("routeChangeComplete", closeUserSideBar);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
      Router.events.off("routeChangeComplete", closeUserSideBar);
    };
  }, [showUserSidebar]);

  useEffect(() => {
    function handler() {
      setShowUserSidebar(true);
    }
    document.addEventListener("opennewusersearchbar", handler);

    return () => document.removeEventListener("opennewusersearchbar", handler);
  }, []);

  useEffect(() => {
    if (showUserSidebar) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
      toggleShowUserNotification(false);
    };
  }, [showUserSidebar]);

  const fetchMoreData = () => {
    fetchMoreMsgs({
      variables: {
        offset: msgsOffset,
      },
    }).then(({ data }) => {
      const newEntries = data?.Chat?.threads?.list || [];
      const lastOffset = data?.Chat?.threads?.offset || null;
      setMsgsOffset(lastOffset);
      setMsgs((currentListings) => [...currentListings, ...newEntries]);
    });
  };

  const renderSideBarContent = () => {
    return (
      <nav className={styles.sidebarV2}>
        <div className={styles.userNotification}>
          <div className={styles.header}>
            <button
              className={styles.closeBtn}
              onClick={() => setShowUserSidebar(false)}
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.7009 15.2839L8.91865 10.5008L4.13642 15.2839C3.51442 15.9053 2.50745 15.9056 1.88545 15.2839C1.26445 14.663 1.26345 13.6545 1.88545 13.0331L6.66767 8.24998L1.88545 3.46702C1.26345 2.84522 1.26345 1.83746 1.88545 1.21616C2.50545 0.594863 3.51442 0.594362 4.13642 1.21616L8.91865 5.99912L13.7009 1.21616C14.3229 0.594763 15.3318 0.595263 15.9528 1.21616C16.5748 1.83796 16.5748 2.84552 15.9528 3.46702L11.1696 8.24998L15.9528 13.0331C16.5748 13.6549 16.5748 14.6625 15.9528 15.2839C15.3318 15.9052 14.3229 15.9056 13.7009 15.2839Z"
                  fill="#4D4D4D"
                  stroke="white"
                />
              </svg>
            </button>
            <p className={styles.title}>حسابي</p>
            <Link href="/user/wallet">
              <div className={styles.wallet}>
                <svg
                  width="18"
                  height="16"
                  viewBox="0 0 18 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.9519 2.9148L11.36 0.269422C11.2847 0.14288 11.1598 0.0523382 11.0147 0.0163392C10.8695 -0.0185689 10.7143 0.00215749 10.5849 0.0774281L5.71704 2.90826L12.9519 2.9148Z"
                    fill="#4D4D4D"
                  />
                  <path
                    d="M2.25 3.63606C1.83713 3.63606 1.49963 3.30879 1.49963 2.90844C1.49963 2.50809 1.83713 2.18083 2.25 2.18083H4.77788L7.28325 0.726685H2.25C1.13963 0.726685 0.217125 1.51212 0.037125 2.54518C0.0225 2.603 0 2.6619 0 2.72736V13.8183C0 15.0183 1.0125 16.0001 2.25 16.0001H14.9996C15.8243 16.0001 16.4993 15.3456 16.4993 14.5459V13.0918H14.625C12.7654 13.0918 11.25 11.6224 11.25 9.81916C11.25 8.01594 12.7654 6.54652 14.625 6.54652H16.5004V5.09238C16.5004 4.29277 15.8254 3.63824 15.0008 3.63824H2.25V3.63606ZM14.2504 2.18192C14.2504 1.44776 13.6879 0.836863 12.9533 0.741957L14.2504 2.90953V2.18192Z"
                    fill="#4D4D4D"
                  />
                  <path
                    d="M17.4377 7.63617H14.6252C13.3844 7.63617 12.3752 8.61469 12.3752 9.81793C12.3752 11.0212 13.3844 11.9997 14.6252 11.9997H17.4377C17.7482 11.9997 18.0002 11.7553 18.0002 11.4542V8.18161C18.0002 7.88053 17.7482 7.63617 17.4377 7.63617ZM14.6252 10.5445C14.2112 10.5445 13.8749 10.2183 13.8749 9.81684C13.8749 9.41648 14.2112 9.09031 14.6252 9.09031C15.0392 9.09031 15.3756 9.41539 15.3756 9.81793C15.3756 10.2194 15.0392 10.5445 14.6252 10.5445Z"
                    fill="#4D4D4D"
                  />
                </svg>

                <span>
                {user?.points_balance
                  ? "ريال " + financialFormat(user?.points_balance * 0.01)
                  : "لا يوجد رصيد"}
              </span>
              </div>
            </Link>
          </div>

          <div className={styles.content}>
            <div className={styles.linksBox}>
              {user?.type === 1 && (
                <Link
                  href="/user/establishment-account"
                  className={styles.linkItem}
                >
                  <div className={styles.right}>
                    <img
                      className={style.companyLogo}
                      src={buildFileSrc(
                        `users/${user?.company_logo}`,
                        config.IMAGES_CDN_BASE_URL,
                      )}
                      alt="company logo"
                      width={40}
                      height={40}
                    />
                    <div className={styles.text}>
                      <p className={styles.title}>{user?.company_name}</p>
                      <p className={styles.subTitle}>حساب المنشأة</p>
                    </div>
                  </div>
                  <Icon width={19} height={19} name="leftArrow-searches" />
                </Link>
              )}
              <Link
                href={`/user/${user?.user_id}`}
                className={styles.linkItem}
                onClick={() => {
                  trackOpenUserProfile({
                    source: "user_side_bar",
                  });
                  setShowUserSidebar(!showUserSidebar);
                }}
              >
                <div className={styles.right}>
                  <img
                    alt={user?.name}
                    src={buildFileSrc(
                      `users/${user?.img}`,
                      config.IMAGES_CDN_BASE_URL,
                    )}
                  />
                  <div className={styles.text}>
                    <p className={styles.title}>{user?.name}</p>
                    <p className={styles.subTitle}>{user?.phone}</p>
                  </div>
                </div>
                <Icon width={19} height={19} name="leftArrow-searches" />
              </Link>
            </div>

            <div className={styles.buttonsRow}>
              <Link href="/الإعلانات-المفضلة" className={styles.btn}>
                <Icon name="Hearted" width={15} height={15} />
                <span>المفضلة</span>
              </Link>
              <Link
                href="#"
                className={styles.btn}
                onClick={(e) => {
                  e.preventDefault();
                  trackOpenNotifs({
                    hasNotifs: !!msgsCount,
                  });
                  toggleShowUserNotification((prev) => !prev);
                  updateAppContext({ msgsCount: 0 });
                }}
              >
                <Icon name="Notifications" width={15} height={15} />
                <span>التنبيهات</span>
                {!!msgsCount && (
                  <div className={styles.newMsgsBadge}>{msgsCount}</div>
                )}
              </Link>
            </div>

            <div className={styles.linksBox}>
              <Link href="/aqar-plus" className={styles.linkItem}>
                <div className={styles.right}>
                  <Icon name="Aqar_plus" width={36} height={30} />
                  <div className={styles.text}>
                    <p className={styles.title}>عقار +</p>
                    <p className={styles.subTitle}>مجموعة من الخدمات المميزة</p>
                  </div>
                </div>
                <Icon width={19} height={19} name="leftArrow-searches" />
              </Link>
            </div>

            <div className={styles.linksBox}>
              <Link href="/user/promotion" className={styles.linkItem}>
                <div className={styles.right}>
                  <Icon name="promotion" width={36} height={36} />
                  <div className={styles.text}>
                    <p className={styles.title}>خدمات الترويج</p>
                    <p className={styles.subTitle}>
                      خدمات تسرع عملية بيع أو تأجير العقار
                    </p>
                  </div>
                </div>
                <Icon width={19} height={19} name="leftArrow-searches" />
              </Link>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <svg
                  width="16"
                  height="14"
                  viewBox="0 0 16 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.16061 13.1542C5.16061 13.5598 4.8288 13.8916 4.42326 13.8916H1.37554C0.969992 13.8916 0.638184 13.5598 0.638184 13.1542V9.81157C0.638184 9.40603 0.969992 9.07422 1.37554 9.07422H4.42326C4.8288 9.07422 5.16061 9.40603 5.16061 9.81157V13.1542Z"
                    fill="#4D4D4D"
                  />
                  <path
                    d="M10.5805 13.1542C10.5805 13.5597 10.2487 13.8915 9.84318 13.8915H6.79546C6.38991 13.8915 6.05811 13.5597 6.05811 13.1542V8.04186C6.05811 7.63631 6.38991 7.3045 6.79546 7.3045H9.84318C10.2487 7.3045 10.5805 7.63631 10.5805 8.04186V13.1542Z"
                    fill="#4D4D4D"
                  />
                  <path
                    d="M15.9995 13.1541C15.9995 13.5597 15.6677 13.8915 15.2621 13.8915H12.2144C11.8089 13.8915 11.4771 13.5597 11.4771 13.1541V6.07555C11.4771 5.67 11.8089 5.3382 12.2144 5.3382H15.2621C15.6677 5.3382 15.9995 5.67 15.9995 6.07555V13.1541Z"
                    fill="#4D4D4D"
                  />
                  <path
                    d="M15.3621 0.0137546L12.0502 0.819533C11.7874 0.883142 11.7288 1.09162 11.92 1.28279L12.6267 1.973C12.6861 2.04064 12.6157 2.1306 12.5598 2.16869C12.1148 2.47248 11.4086 2.91824 10.4324 3.42755C5.1178 6.20034 0.73679 6.03026 0.67092 6.02726C0.319252 6.00941 0.0185599 6.28032 0.000371824 6.63258C-0.017767 6.98503 0.253185 7.28553 0.60564 7.30366C0.631546 7.30499 0.736889 7.30976 0.914001 7.30976C2.04987 7.30976 6.13008 7.11377 11.0236 4.56071C12.1815 3.95662 12.984 3.43561 13.4526 3.10577C13.5189 3.05912 13.6344 2.9787 13.7409 3.08719L14.5139 3.86013C14.7051 4.0513 14.9125 3.99246 14.9751 3.72942L15.7264 0.376384C15.7889 0.113297 15.6249 -0.0498544 15.3621 0.0137546Z"
                    fill="#4D4D4D"
                  />
                </svg>
                <span>الأنشطة</span>
              </div>
              <Link href="/add-listing" style={{ marginTop: -4 }}>
                <UIButton theme="primary" className={styles.fwBtn}>
                  <Icon width={14} height={14} name="Add-white" />
                  <span>إضافة</span>
                </UIButton>
              </Link>
              <div className={styles.linksBox}>
                {ACTIVITIES_LINKS.filter((link) => link.filter(user)).map(
                  (activityLink) => {
                    return (
                      <Link
                        href={
                          typeof activityLink.link === "string"
                            ? activityLink.link
                            : activityLink.link(user)
                        }
                        className={styles.linkItem}
                      >
                        <div className={styles.right}>
                          <div className={styles.text}>
                            <p className={styles.title}>{activityLink.title}</p>
                            <p className={styles.subTitle}>
                              {activityLink.subTitle}
                            </p>
                          </div>
                        </div>
                        <Icon
                          width={19}
                          height={19}
                          name="leftArrow-searches"
                        />
                      </Link>
                    );
                  },
                )}
              </div>
            </div>

            {/*<div className={styles.section}>*/}
            {/*  <div className={styles.sectionTitle}>*/}
            {/*    <svg*/}
            {/*      width="16"*/}
            {/*      height="15"*/}
            {/*      viewBox="0 0 16 15"*/}
            {/*      fill="none"*/}
            {/*      xmlns="http://www.w3.org/2000/svg"*/}
            {/*    >*/}
            {/*      <path*/}
            {/*        d="M5.99938 8.53233C7.65616 8.53233 8.99925 7.18924 8.99925 5.53246C8.99925 3.87568 7.65616 2.53259 5.99938 2.53259C4.3426 2.53259 2.99951 3.87568 2.99951 5.53246C2.99951 7.18924 4.3426 8.53233 5.99938 8.53233Z"*/}
            {/*        fill="#4D4D4D"*/}
            {/*      />*/}
            {/*      <path*/}
            {/*        d="M11.824 11.6533C8.44914 8.82047 3.54835 8.82047 0.173504 11.6533C0.0635089 11.7463 -0.000488281 11.8843 -0.000488281 12.0293V13.4103C-0.000488281 14.0862 0.447492 14.5322 0.999467 14.5322H10.999C11.551 14.5322 11.999 14.0862 11.999 13.5333V12.1753C11.999 11.8843 11.935 11.7463 11.824 11.6533Z"*/}
            {/*        fill="#4D4D4D"*/}
            {/*      />*/}
            {/*      <path*/}
            {/*        d="M9.99972 5.53249C9.99972 5.87348 9.95572 6.20446 9.87473 6.51945C9.91773 6.52145 9.95672 6.53245 9.99972 6.53245C11.6536 6.53245 12.9996 5.18651 12.9996 3.53258C12.9996 1.87866 11.6536 0.532715 9.99972 0.532715C8.97377 0.532715 8.06881 1.05169 7.52783 1.83866C8.97677 2.44063 9.99972 3.86857 9.99972 5.53249Z"*/}
            {/*        fill="#4D4D4D"*/}
            {/*      />*/}
            {/*      <path*/}
            {/*        d="M15.8251 9.65349C13.9852 8.10956 11.6923 7.41359 9.44043 7.55359C9.14844 8.04456 8.75946 8.47255 8.29248 8.80153C9.80341 9.15552 11.2513 9.85548 12.5053 10.9174C12.8293 11.1914 13.0003 11.6084 13.0003 12.0324V12.5324H15.0002C15.5522 12.5324 16.0001 12.0844 16.0001 11.5324V10.0325C16.0001 9.88748 15.9361 9.74649 15.8251 9.65349Z"*/}
            {/*        fill="#4D4D4D"*/}
            {/*      />*/}
            {/*    </svg>*/}
            {/*    <span>العملاء</span>*/}
            {/*  </div>*/}
            {/*  <div className={styles.linksBox}>*/}
            {/*    <Link*/}
            {/*      href={`/user/${user?.user_id}`}*/}
            {/*      className={styles.linkItem}*/}
            {/*    >*/}
            {/*      <div className={styles.right}>*/}
            {/*        <div className={styles.text}>*/}
            {/*          <p className={styles.title}>عملائي</p>*/}
            {/*          <p className={styles.subTitle}>متابعة العملاء وطلباتهم</p>*/}
            {/*        </div>*/}
            {/*      </div>*/}
            {/*      <Icon width={19} height={19} name="leftArrow-searches" />*/}
            {/*    </Link>*/}
            {/*  </div>*/}
            {/*</div>*/}

            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.3995 4.34949H1.59951C0.719512 4.34949 -0.000488281 5.04852 -0.000488281 5.90289V14.4466C-0.000488281 15.301 0.719512 16 1.59951 16H14.3995C15.2795 16 15.9995 15.301 15.9995 14.4466V5.90289C15.9995 5.04852 15.2795 4.34949 14.3995 4.34949ZM6.79951 13.2815L3.99951 10.5631L5.11951 9.47571L6.79951 11.1068L10.9595 7.06794L12.0795 8.15532L6.79951 13.2815Z"
                    fill="#4D4D4D"
                  />
                  <path
                    d="M11.2003 3.1068V1.5534L9.60029 0H6.40029L4.80029 1.5534V3.1068H11.2003ZM6.40029 1.5534H9.60029V3.1068H6.40029V1.5534Z"
                    fill="#4D4D4D"
                  />
                </svg>

                <span>المكاتب</span>
              </div>
              <div className={styles.linksBox}>
                {OFFICE_LINKS.filter((link) => link.filter(user)).map(
                  (officeLink) => {
                    const isExternal = /\/offices-management/.test(
                      typeof officeLink.link === "string"
                        ? officeLink.link
                        : officeLink.link(user),
                    );

                    return (
                      <Link
                        href={
                          typeof officeLink.link === "string"
                            ? officeLink.link
                            : officeLink.link(user)
                        }
                        className={styles.linkItem}
                        onClick={
                          isExternal
                            ? () => {
                                e.preventDefault();
                                window.open(
                                  officeLink.link,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              }
                            : undefined
                        }
                      >
                        <div className={styles.right}>
                          <div className={styles.text}>
                            <p className={styles.title}>{officeLink.title}</p>
                          </div>
                        </div>
                        <Icon
                          width={19}
                          height={19}
                          name="leftArrow-searches"
                        />
                      </Link>
                    );
                  },
                )}
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <svg
                  width="16"
                  height="17"
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.99924 3.92896C5.79325 3.92896 3.99902 5.72292 3.99902 7.92891C3.99902 10.1349 5.79325 11.9289 7.99924 11.9289C10.2052 11.9289 11.9984 10.1349 11.9984 7.92891C11.9984 5.72292 10.2052 3.92896 7.99924 3.92896ZM7.9159 7.42888H8.08257C8.77189 7.42888 9.33264 7.98963 9.33264 8.67895C9.33264 9.22159 8.98323 9.67948 8.49926 9.85219V10.0954C8.49926 10.3715 8.2752 10.5955 7.99924 10.5955C7.72317 10.5955 7.49921 10.3715 7.49921 10.0954V9.92901H7.16586C6.88979 9.92901 6.66583 9.70495 6.66583 9.42899C6.66583 9.15292 6.88979 8.92896 7.16586 8.92896H8.08257C8.2205 8.92896 8.33259 8.81687 8.33259 8.67895C8.33259 8.54091 8.2205 8.42893 8.08257 8.42893H7.9159C7.22658 8.42893 6.66583 7.86818 6.66583 7.17887C6.66583 6.63622 7.01524 6.17822 7.49921 6.00563V5.76239C7.49921 5.48632 7.72317 5.2621 7.99924 5.2621C8.2752 5.2621 8.49926 5.48632 8.49926 5.76239V5.9288H8.83261C9.10857 5.9288 9.33264 6.15287 9.33264 6.42883C9.33264 6.70489 9.10857 6.92885 8.83261 6.92885H7.9159C7.77797 6.92885 7.66588 7.04083 7.66588 7.17887C7.66588 7.3169 7.77797 7.42888 7.9159 7.42888Z"
                    fill="#4D4D4D"
                  />
                  <path
                    d="M14.8463 7.44348C14.5702 7.44348 14.3462 7.66755 14.3462 7.94351C14.3462 10.9335 12.2652 13.4409 9.47723 14.1102V13.4868C9.47723 13.0428 8.93939 12.8183 8.62406 13.1336L7.31983 14.4376C7.12451 14.6329 7.12451 14.9496 7.31983 15.1449L8.62406 16.4489C8.93672 16.7616 9.47723 16.5427 9.47723 16.0955V15.1415C12.8226 14.4555 15.3463 11.4889 15.3463 7.94351C15.3463 7.66755 15.1222 7.44348 14.8463 7.44348Z"
                    fill="#4D4D4D"
                  />
                  <path
                    d="M5.88127 13.9217C3.35057 13.035 1.65083 10.6328 1.65083 7.94353C1.65083 7.18221 1.78761 6.44311 2.04565 5.74836L2.64568 6.09369C3.02906 6.31567 3.49372 5.96292 3.37905 5.5309L2.87173 3.64174C2.7992 3.37633 2.5326 3.21899 2.2592 3.28833L0.368475 3.79435C-0.0615211 3.90966 -0.135343 4.48894 0.248677 4.71028L1.16852 5.24104C0.829833 6.09175 0.651823 7.00353 0.651823 7.94353C0.651823 11.0562 2.61979 13.8383 5.55104 14.8655C5.81569 14.9572 6.0979 14.8165 6.18857 14.559C6.27925 14.2983 6.14165 14.013 5.88127 13.9217Z"
                    fill="#4D4D4D"
                  />
                  <path
                    d="M15.369 3.87348L14.2939 4.16152C12.9853 1.99959 10.6001 0.595703 7.99882 0.595703C6.32417 0.595703 4.75432 1.14636 3.45899 2.18641C3.24362 2.35975 3.20931 2.6742 3.38191 2.88957C3.55589 3.10559 3.87085 3.13889 4.08611 2.9664C5.20212 2.06971 6.55479 1.59576 7.99882 1.59576C10.1468 1.59576 12.122 2.70516 13.2793 4.43315L12.6959 4.58888C12.2666 4.7042 12.1915 5.28245 12.5761 5.50508L14.27 6.48378C14.3853 6.54991 14.5205 6.56791 14.6492 6.53378C14.7765 6.49964 14.8856 6.41601 14.9523 6.30069L15.9315 4.60659C16.1522 4.22321 15.8023 3.75891 15.369 3.87348Z"
                    fill="#4D4D4D"
                  />
                </svg>

                <span>المعلومات المالية</span>
              </div>
              <div className={styles.linksBox}>
                {FINANCIAL_LINKS.filter((link) => link.filter(user)).map(
                  (financialLink) => {
                    return (
                      <Link
                        href={
                          typeof financialLink.link === "string"
                            ? financialLink.link
                            : financialLink.link(user)
                        }
                        className={styles.linkItem}
                      >
                        <div className={styles.right}>
                          <div className={styles.text}>
                            <p className={styles.title}>
                              {typeof financialLink.title === "string"
                                ? financialLink.title
                                : financialLink.title(user)}
                            </p>
                          </div>
                        </div>
                        <Icon
                          width={19}
                          height={19}
                          name="leftArrow-searches"
                        />
                      </Link>
                    );
                  },
                )}
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <svg
                  width="16"
                  height="17"
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.0082 4.1068V2.5534L9.31387 1H5.92526L4.23096 2.5534V4.1068H11.0082ZM5.92526 2.5534H9.31387V4.1068H5.92526V2.5534Z"
                    fill="#4D4D4D"
                  />
                  <path
                    d="M15.9995 13.238C11.4783 17.1694 4.52084 17.1694 -0.000488281 13.238C0.742524 10.9469 3.09079 9.46656 5.61633 9.69739H10.3827C12.9045 9.48054 15.2443 10.9556 15.9995 13.238ZM12.1917 4.14294C12.1917 0.624845 7.68786 -1.13637 5.05377 1.351C2.41979 3.83837 4.28564 8.09166 8.01109 8.09166C10.3204 8.09166 12.1917 6.32375 12.1917 4.14294Z"
                    fill="#4D4D4D"
                  />
                </svg>

                <span>إدارة الحساب</span>
              </div>
              <div className={styles.linksBox}>
                {PROFILE_LINKS.filter((link) => link.filter(user)).map(
                  (profileLink) => {
                    return (
                      <Link
                        href={
                          typeof profileLink.link === "string"
                            ? profileLink.link
                            : profileLink.link(user)
                        }
                        className={styles.linkItem}
                      >
                        <div className={styles.right}>
                          <div className={styles.text}>
                            <p className={styles.title}>{profileLink.title}</p>
                          </div>
                        </div>
                        <Icon
                          width={19}
                          height={19}
                          name="leftArrow-searches"
                        />
                      </Link>
                    );
                  },
                )}
                <Link
                  href=""
                  className={styles.linkItem}
                  onClick={(e) => {
                    path.updateContext({
                      basePath: "",
                      category: null,
                      city: null,
                      page: null,
                      listing:null,
                      direction: null,
                      district: null,
                      filters: {},
                      fields: [],
                      searchFields: [],
                      extendedFields: [],
                    });
                    router.push("/");
                    updateContext({ user: null, token: null, userCards: [] });

                    logoutMutation({ context: { token } })
                      .then(() => {
                        setLoginCookies("", {});
                        setCookie("user", null);
                        updateContext({
                          user: null,
                          token: null,
                          userCards: [],
                        });
                      })
                      .catch(() => {
                        updateContext({ token, user, ...restAuth });
                      });

                    e.target.dispatchEvent(
                      new CustomEvent(INTERCOM_EVENT, {
                        bubbles: true,
                        detail: { type: "shutdown" },
                      }),
                    );
                    apolloClient.resetStore();
                    trackLogoutEvent();
                    if (showUserSidebar) setShowUserSidebar(!showUserSidebar);
                  }}
                >
                  <div className={styles.right}>
                    <div className={styles.text}>
                      <p className={styles.title} style={{ color: "#EB605A" }}>
                        تسجيل الخروج
                      </p>
                    </div>
                  </div>
                  <Icon width={19} height={19} name="leftArrow-searches" />
                </Link>
              </div>
            </div>

            <button
              className={styles.contactUsLink}
              onClick={(e) => {
                e.preventDefault();
                e.target.dispatchEvent(
                  new CustomEvent(INTERCOM_EVENT, {
                    bubbles: true,
                    detail: { type: "show" },
                  }),
                );
                trackOpenContactUs({ source: "user_side_bar" });
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.76893 4.23828C2.60272 4.23828 -0.000459486 6.41558 -0.000459486 9.09196C-0.000459486 10.1618 0.414509 11.249 1.17551 12.0918L0.100876 13.3198C-0.00486182 13.4408 -0.0305102 13.6126 0.0363998 13.7592C0.1029 13.9058 0.248696 13.9999 0.409696 13.9999H6.17908C8.51684 13.9999 11.5383 11.9869 11.5383 9.09196C11.5383 6.41558 8.93514 4.23828 5.76893 4.23828ZM3.2807 9.84374C2.82849 9.84374 2.46042 9.47564 2.46042 9.02346C2.46042 8.57125 2.82852 8.20318 3.2807 8.20318C3.73289 8.20318 4.10099 8.57128 4.10099 9.02346C4.10102 9.47564 3.73292 9.84374 3.2807 9.84374ZM5.76893 9.84374C5.31672 9.84374 4.94864 9.47564 4.94864 9.02346C4.94864 8.57125 5.31675 8.20318 5.76893 8.20318C6.22111 8.20318 6.58921 8.57128 6.58921 9.02346C6.58921 9.47564 6.22114 9.84374 5.76893 9.84374ZM8.25715 9.84374C7.80494 9.84374 7.43687 9.47564 7.43687 9.02346C7.43687 8.57125 7.80497 8.20318 8.25715 8.20318C8.70934 8.20318 9.07744 8.57128 9.07744 9.02346C9.07744 9.47564 8.70936 9.84374 8.25715 9.84374Z"
                  fill="#3678EE"
                />
                <path
                  d="M13.9004 8.3459L12.9348 7.21921C13.6241 6.44139 13.9994 5.49289 13.9994 4.5116C13.9994 2.0239 11.6074 0 8.66748 0C6.00899 0 3.75037 1.65673 3.35156 3.81308C4.09334 3.56103 4.92522 3.41775 5.76913 3.41775C9.36081 3.41775 12.3117 5.92639 12.3548 9.02321H13.5892C13.9396 9.02324 14.1277 8.61067 13.9004 8.3459Z"
                  fill="#3678EE"
                />
              </svg>
              <p>بحاجة للمساعدة؟ تواصل معنا</p>
            </button>
          </div>
        </div>
      </nav>
    );
  };

  return (
    shouldRender && (
      <div className={styles.container}>
        <div
          className={cx("overlay", {
            hidden: !showUserSidebar,
          })}
        />
        <div
          className={cx("sidebarContainer", {
            hidden: !showUserSidebar,
          })}
          ref={UserSidebarRef}
        >
          {showUserNotification ? (
            <nav className={styles.userNotificationBar}>
              <div className={styles.notificationHeader}>
                <Icon
                  className={styles.headerIcon}
                  name="OutlinedClose"
                  width={25}
                  height={25}
                  onClick={() => toggleShowUserNotification((prev) => !prev)}
                />
                <span className={styles.title}>التنبيهات</span>
              </div>
              <hr />
              <div className={styles.notificationBody} id="notification">
                <InfiniteScroll
                  dataLength={msgs.length}
                  next={fetchMoreData}
                  scrollableTarget="notification"
                  hasMore={msgsOffset !== null}
                  loader={
                    <div className={styles.spinnerWrapper}>
                      <Spinner className={styles.spinner3rem} />
                    </div>
                  }
                >
                  {msgs?.length ? (
                    msgs.map((msg) => {
                      return (
                        <div className={styles.msgContent}>
                          <p>
                            {" "}
                            {dayjs(msg.send_time).format(
                              "HH:mm🕒 DD/MM/YYYY📅",
                            )}
                          </p>
                          <div
                            {...{
                              dangerouslySetInnerHTML: {
                                __html: linkify(msg.content),
                              },
                            }}
                          />
                        </div>
                      );
                    })
                  ) : (
                    <div>
                      {!loading && (
                        <div className={styles.emptyListingsNote}>
                          لا يوجد لديك مسجات حالياُ.
                        </div>
                      )}
                    </div>
                  )}
                </InfiniteScroll>
              </div>
            </nav>
          ) : (
            renderSideBarContent()
          )}
        </div>
      </div>
    )
  );
};

export default UserSidebarV2;
